import { useMutation, useQuery } from "@tanstack/react-query";
import { z } from "zod";
import { api } from "@/lib/api";

/**
 * Loose schemas for the Companies House proxy endpoints in Sidequester
 * Dashboard group. We use `passthrough` because Companies House response
 * shapes are large and we only consume a handful of fields.
 */
export const chSearchItemSchema = z
  .object({
    company_number: z.string(),
    title: z.string(),
    company_status: z.string().optional().default(""),
    address_snippet: z.string().optional().default(""),
    date_of_creation: z.string().optional().default(""),
    company_type: z.string().optional().default(""),
  })
  .passthrough();
export type CompaniesHouseSearchItem = z.infer<typeof chSearchItemSchema>;

export const chSearchResultSchema = z
  .object({
    items: z.array(chSearchItemSchema).default([]),
    total_results: z.number().optional(),
  })
  .passthrough();
export type CompaniesHouseSearchResult = z.infer<typeof chSearchResultSchema>;

/**
 * Subset of the Companies House profile we actually use to pre-fill the
 * Add Company form. `passthrough` keeps the rest so we can opportunistically
 * persist `directors`/`pscs`/`accounts.next_due` later.
 */
export const chProfileSchema = z
  .object({
    company_number: z.string(),
    company_name: z.string(),
    type: z.string().optional().default(""),
    company_status: z.string().optional().default(""),
    date_of_creation: z.string().optional().default(""),
    sic_codes: z.array(z.string()).optional().default([]),
    registered_office_address: z
      .object({
        address_line_1: z.string().optional(),
        address_line_2: z.string().optional(),
        locality: z.string().optional(),
        region: z.string().optional(),
        postal_code: z.string().optional(),
        country: z.string().optional(),
      })
      .passthrough()
      .optional(),
    accounts: z
      .object({
        next_due: z.string().optional(),
        next_accounts: z
          .object({
            period_end_on: z.string().optional(),
            due_on: z.string().optional(),
          })
          .passthrough()
          .optional(),
        accounting_reference_date: z
          .object({ day: z.string().optional(), month: z.string().optional() })
          .passthrough()
          .optional(),
      })
      .passthrough()
      .optional(),
    confirmation_statement: z
      .object({ next_due: z.string().optional() })
      .passthrough()
      .optional(),
  })
  .passthrough();
export type CompaniesHouseProfile = z.infer<typeof chProfileSchema>;

export const chKeys = {
  search: (q: string) => ["ch-search", q] as const,
  profile: (n: string) => ["ch-profile", n] as const,
};

/** Debounce-friendly search hook. Pass empty string to disable. */
export function useCompaniesHouseSearch(q: string) {
  return useQuery({
    queryKey: chKeys.search(q),
    enabled: q.trim().length >= 2,
    staleTime: 60_000,
    queryFn: async (): Promise<CompaniesHouseSearchResult> => {
      const data = await api.get<unknown>("/companies-house/search", {
        query: { q, items_per_page: 8 },
      });
      return chSearchResultSchema.parse(data);
    },
  });
}

/** One-shot fetch by company number, used after picking a search result. */
export function useFetchCompaniesHouseProfile() {
  return useMutation({
    mutationFn: async (companyNumber: string): Promise<CompaniesHouseProfile> => {
      const data = await api.get<unknown>(
        `/companies-house/${encodeURIComponent(companyNumber)}`,
      );
      return chProfileSchema.parse(data);
    },
  });
}
